import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { Unit, useUnitStore } from '../../store/useUnitStore';
import { Family, useFamilyStore } from '../../store/useFamilyStore';
import { Button, Label, TextInput } from 'flowbite-react';
import { HiPencil, HiPlus } from 'react-icons/hi';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../../components/ui/table';

export default function UnitDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const getUnit = useUnitStore((state) => state.getUnit);
  const updateUnit = useUnitStore((state) => state.updateUnit);
  const loading = useUnitStore((state) => state.loading);
  const getFamilies = useFamilyStore((state) => state.getFamilies);
  const families = useFamilyStore((state) => state.families);

  const [unit, setUnit] = useState<Unit | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [name, setName] = useState('');
  const [unitNumber, setUnitNumber] = useState('');

  useEffect(() => {
    if (id) {
      loadUnit();
      getFamilies();
    }
  }, [id]);

  const loadUnit = async () => {
    if (!id) return;
    const unitData = await getUnit(id);
    if (unitData) {
      setUnit(unitData);
      setName(unitData.name);
      setUnitNumber(unitData.unitNumber);
    }
  };

  const handleUpdateUnit = async () => {
    if (!name || !unitNumber) {
      toast.error('Please fill in all required fields.', {
        duration: 2000,
        icon: '❌',
      });
      return;
    }

    if (!id) return;
    await updateUnit(id, { name, unitNumber });
    setIsEditMode(false);
    loadUnit();
  };

  const handleAddFamily = () => {
    navigate('/families', { state: { preselectedUnit: id } });
  };

  const getUnitFamilies = (): Family[] => {
    if (!id) return [];
    return families.filter((family) => {
      const familyUnitId = typeof family.unit === 'string' ? family.unit : family.unit?._id;
      return familyUnitId === id;
    });
  };

  const unitFamilies = getUnitFamilies();

  if (!unit) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-500" style={{ fontFamily: 'Lato, sans-serif' }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageMeta title="Unit Details" description="View and manage unit information" />
      <PageBreadcrumb pageTitle="Unit Details" />

      <div className="space-y-6">
        <ComponentCard title={isEditMode ? 'Edit Unit' : 'Unit Information'}>
          <div className="space-y-6">
            {/* Header with Edit Button */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {unit.name}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Unit Number: {unit.unitNumber}
                </p>
              </div>
              {!isEditMode && (
                <Button
                  onClick={() => setIsEditMode(true)}
                  className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                  style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <HiPencil className="mr-2" /> Edit Unit
                  </span>
                </Button>
              )}
            </div>

            {/* Unit Information */}
            {isEditMode ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" value="Unit Name *" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <TextInput
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  />
                </div>
                <div>
                  <Label htmlFor="unitNumber" value="Unit Number *" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <TextInput
                    id="unitNumber"
                    type="text"
                    value={unitNumber}
                    onChange={(e) => setUnitNumber(e.target.value)}
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  />
                </div>
                <div className="col-span-2 flex gap-2">
                  <Button
                    onClick={handleUpdateUnit}
                    disabled={loading}
                    className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                    style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    onClick={() => {
                      setIsEditMode(false);
                      setName(unit.name);
                      setUnitNumber(unit.unitNumber);
                    }}
                    color="gray"
                    style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label value="Unit Name" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {unit.name}
                  </p>
                </div>
                <div>
                  <Label value="Unit Number" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {unit.unitNumber}
                  </p>
                </div>
                <div>
                  <Label value="Created At" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {dayjs(unit.createdAt).format('DD MMM YYYY, hh:mm A')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </ComponentCard>

        {/* Families in this Unit */}
        <ComponentCard title={`Families (${unitFamilies.length})`}>
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
                Manage families assigned to this unit
              </p>
              <Button
                onClick={handleAddFamily}
                size="sm"
                className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <HiPlus className="mr-2" /> Add Family
                </span>
              </Button>
            </div>

            {unitFamilies.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
                  No families in this unit yet.
                </p>
                <Button
                  onClick={handleAddFamily}
                  className="mt-4 bg-[#B8860B] text-white hover:bg-[#9A7209]"
                  style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <HiPlus className="mr-2" /> Add First Family
                  </span>
                </Button>
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
                <div className="max-w-full overflow-x-auto">
                  <div className="min-w-[800px]">
                    <Table>
                      <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                        <TableRow>
                          <TableCell
                            isHeader
                            className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            style={{ fontFamily: 'Playfair Display, serif' }}
                          >
                            Family Name
                          </TableCell>
                          <TableCell
                            isHeader
                            className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            style={{ fontFamily: 'Playfair Display, serif' }}
                          >
                            Created At
                          </TableCell>
                          <TableCell
                            isHeader
                            className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            style={{ fontFamily: 'Playfair Display, serif' }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHeader>

                      <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                        {unitFamilies.map((family) => (
                          <TableRow key={family._id}>
                            <div
                              className="contents cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                              onClick={() => navigate(`/families/${family._id}`)}
                            >
                              <TableCell
                                className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                                style={{ fontFamily: 'Lato, sans-serif' }}
                              >
                                {family.familyName}
                              </TableCell>
                              <TableCell
                                className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                                style={{ fontFamily: 'Lato, sans-serif' }}
                              >
                                {dayjs(family.createdAt).format('DD MMM YYYY')}
                              </TableCell>
                              <TableCell className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                                <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                                  <Button
                                    size="sm"
                                    className="bg-blue-500 text-white hover:bg-blue-600"
                                    onClick={() => navigate(`/families/${family._id}`)}
                                    style={{
                                      fontFamily: 'Lato, sans-serif',
                                      height: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    View Details
                                  </Button>
                                </div>
                              </TableCell>
                            </div>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ComponentCard>
      </div>
    </>
  );
}
